@if (isActive()) {
  <section class="checklist-list">
    <div class="checklist-list__header">
      <div>
        <h2 class="checklist-list__title">{{ i18n.t('checklistList.title') }}</h2>
        <p class="checklist-list__meta">{{ i18n.t('checklistList.meta') }}</p>
      </div>

      <div class="app-view-toggle" role="tablist" [attr.aria-label]="i18n.t('checklistList.viewMode.label')">
        <button
          class="app-view-toggle__button"
          type="button"
          [class.app-view-toggle__button--active]="viewMode() === 'list'"
          [attr.aria-pressed]="viewMode() === 'list'"
          [attr.aria-label]="i18n.t('checklistList.viewMode.list')"
          [attr.title]="i18n.t('checklistList.viewMode.list')"
          (click)="setViewMode('list')"
        >
          <span class="fa-solid fa-table-list" aria-hidden="true"></span>
        </button>
        <button
          class="app-view-toggle__button"
          type="button"
          [class.app-view-toggle__button--active]="viewMode() === 'tiles'"
          [attr.aria-pressed]="viewMode() === 'tiles'"
          [attr.aria-label]="i18n.t('checklistList.viewMode.tiles')"
          [attr.title]="i18n.t('checklistList.viewMode.tiles')"
          (click)="setViewMode('tiles')"
        >
          <span class="fa-solid fa-table-cells-large" aria-hidden="true"></span>
        </button>
      </div>
    </div>

    @if (loadError(); as error) {
      <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
    }

    @if (isLoading()) {
      <div class="app-empty-state"><span class="app-message__icon app-message__icon--loading fa-solid fa-spinner fa-spin" aria-hidden="true"></span> {{ i18n.t('checklistList.states.loading') }}</div>
    } @else if (!checklists().length) {
      <div class="app-empty-state"><span class="app-message__icon app-message__icon--info fa-solid fa-circle-info" aria-hidden="true"></span> {{ i18n.t('checklistList.states.empty') }}</div>
    } @else if (viewMode() === 'list') {
      <div class="app-data-table-shell">
        <table class="app-data-table">
          <thead>
            <tr class="app-data-table__header-row">
              <th>{{ i18n.t('checklistList.columns.title') }}</th>
              <th>{{ i18n.t('checklistList.columns.description') }}</th>
              <th>{{ i18n.t('checklistList.columns.version') }}</th>
              <th>{{ i18n.t('checklistList.columns.status') }}</th>
              <th>{{ i18n.t('checklistList.columns.updatedAt') }}</th>
              <th class="checklist-list__actions-header">{{ i18n.t('checklistList.columns.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            @for (checklist of checklists(); track trackByChecklistId($index, checklist)) {
              <tr>
                <td class="checklist-list__cell-title">
                  <div class="checklist-list__cell-primary">{{ checklist.title }}</div>
                </td>
                <td>{{ checklist.description || i18n.t('checklistList.fallbacks.noDescription') }}</td>
                <td>{{ checklist.version }}</td>
                <td>
                  <span
                    class="app-status-chip"
                    [class.app-status-chip--draft]="checklist.status === 'draft'"
                    [class.app-status-chip--active]="checklist.status === 'active'"
                    [class.app-status-chip--archived]="checklist.status === 'archived'"
                  >
                    {{ getStatusLabel(checklist) }}
                  </span>
                </td>
                <td>{{ checklist.updated_at | date: 'yyyy-MM-dd HH:mm' }}</td>
                <td class="checklist-list__actions-cell">
                  <div class="checklist-list__actions">
                    <button
                      class="secondary-button secondary-button--compact secondary-button--indigo checklist-list__action-button"
                      type="button"
                      [attr.title]="i18n.t('checklistList.actions.edit')"
                      [attr.aria-label]="i18n.t('checklistList.actions.edit')"
                      (click)="editChecklist(checklist)"
                    >
                      <span class="fa-solid fa-pen-to-square" aria-hidden="true"></span>
                    </button>
                    @if (checklist.status === 'active') {
                      <button
                        class="secondary-button secondary-button--compact secondary-button--indigo checklist-list__action-button"
                        type="button"
                        [attr.title]="i18n.t('checklistList.actions.archive')"
                        [attr.aria-label]="i18n.t('checklistList.actions.archive')"
                        (click)="archiveChecklist(checklist)"
                      >
                        <span class="fa-solid fa-box-archive" aria-hidden="true"></span>
                      </button>
                    }
                    @if (checklist.status === 'draft') {
                      <button
                        class="app-button--danger primary-button primary-button--compact checklist-list__action-button checklist-list__action-button--danger"
                        type="button"
                        [attr.title]="i18n.t('checklistList.actions.delete')"
                        [attr.aria-label]="i18n.t('checklistList.actions.delete')"
                        (click)="deleteChecklist(checklist)"
                      >
                        <span class="fa-solid fa-trash-can" aria-hidden="true"></span>
                      </button>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else {
      <div class="app-card-grid">
        @for (checklist of checklists(); track trackByChecklistId($index, checklist)) {
          <article class="app-data-card checklist-list__card">
            <div class="app-data-card__header checklist-list__card-header">
              <h3 class="checklist-list__card-title">{{ checklist.title }}</h3>
            </div>

            <p class="checklist-list__card-description">
              {{ checklist.description || i18n.t('checklistList.fallbacks.noDescription') }}
            </p>

            <dl class="checklist-list__details">
              <div>
                <dt>{{ i18n.t('checklistList.columns.status') }}</dt>
                <dd>
                  <span
                    class="app-status-chip"
                    [class.app-status-chip--draft]="checklist.status === 'draft'"
                    [class.app-status-chip--active]="checklist.status === 'active'"
                    [class.app-status-chip--archived]="checklist.status === 'archived'"
                  >
                    {{ getStatusLabel(checklist) }}
                  </span>
                </dd>
              </div>
              <div>
                <dt>{{ i18n.t('checklistList.columns.version') }}</dt>
                <dd>{{ checklist.version }}</dd>
              </div>
              <div>
                <dt>{{ i18n.t('checklistList.columns.updatedAt') }}</dt>
                <dd>{{ checklist.updated_at | date: 'yyyy-MM-dd HH:mm' }}</dd>
              </div>
            </dl>

            <footer class="app-data-card__footer checklist-list__card-footer">
              <div class="checklist-list__actions">
                <button
                  class="secondary-button secondary-button--compact secondary-button--indigo checklist-list__action-button"
                  type="button"
                  [attr.title]="i18n.t('checklistList.actions.edit')"
                  [attr.aria-label]="i18n.t('checklistList.actions.edit')"
                  (click)="editChecklist(checklist)"
                >
                  <span class="fa-solid fa-pen-to-square" aria-hidden="true"></span>
                </button>
                @if (checklist.status === 'active') {
                  <button
                    class="secondary-button secondary-button--compact secondary-button--indigo checklist-list__action-button"
                    type="button"
                    [attr.title]="i18n.t('checklistList.actions.archive')"
                    [attr.aria-label]="i18n.t('checklistList.actions.archive')"
                    (click)="archiveChecklist(checklist)"
                  >
                    <span class="fa-solid fa-box-archive" aria-hidden="true"></span>
                  </button>
                }
                @if (checklist.status === 'draft') {
                  <button
                    class="app-button--danger primary-button primary-button--compact checklist-list__action-button checklist-list__action-button--danger"
                    type="button"
                    [attr.title]="i18n.t('checklistList.actions.delete')"
                    [attr.aria-label]="i18n.t('checklistList.actions.delete')"
                    (click)="deleteChecklist(checklist)"
                  >
                    <span class="fa-solid fa-trash-can" aria-hidden="true"></span>
                  </button>
                }
                @if (checklist.status !== 'active' && checklist.status !== 'draft') {
                  <span class="app-action-placeholder" aria-hidden="true"></span>
                }
              </div>
            </footer>
          </article>
        }
      </div>
    }
    <app-confirmation-dialog
      [isOpen]="deleteDialogOpen()"
      [title]="i18n.t('checklistList.deleteConfirmation.title')"
      [message]="i18n.t('checklistList.deleteConfirmation.message')"
      [confirmLabel]="isDeleting() ? i18n.t('checklistList.deleteConfirmation.deleting') : i18n.t('common.actions.yes')"
      [cancelLabel]="i18n.t('common.actions.cancel')"
      [confirmDisabled]="isDeleting()"
      (confirmed)="confirmChecklistDeletion()"
      (cancelled)="cancelChecklistDeletion()"
    />
  </section>
}
